// Runtime config. VITE_* env vars are inlined at build time by Vite.
//
// Path layout (locked):
//   - API paths below INCLUDE the `/api/` prefix (e.g. `/api/auth/login`)
//     because the FastAPI backend serves at that prefix.
//   - `VITE_API_BASE_URL` is the API ORIGIN only -- NO `/api` suffix, NO
//     trailing slash. Otherwise axios produces `/api/api/auth/login`.
//
// Production (split-host, locked):
//   VITE_API_BASE_URL = https://api.prowatchly.com
//   VITE_WS_BASE_URL  = wss://api.prowatchly.com
//
// Same-origin nginx-edge (alternative deploy mode):
//   VITE_API_BASE_URL = ""     -> axios fetches relative to current origin
//   VITE_WS_BASE_URL  = ""     -> resolveWSBase derives wss:// from location
//
// Dev (host-side `npm run dev` against local backend):
//   VITE_API_BASE_URL=http://localhost:8000
//   VITE_WS_BASE_URL=ws://localhost:8000

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';
const RAW_WS_BASE = import.meta.env.VITE_WS_BASE_URL ?? 'ws://localhost:8000';

/**
 * Resolve WebSocket base URL.
 *
 * WebSocket constructor REQUIRES an absolute ws:// or wss:// URL. If the
 * build arg is empty, "/", or a relative path, derive an absolute origin
 * from window.location (https → wss, http → ws). This makes the default
 * same-origin nginx-edge deployment work without any per-env build arg.
 */
const resolveWSBase = (raw: string): string => {
  if (typeof window === 'undefined') return raw;
  // Already absolute — caller knows what they want (cross-origin staging etc.)
  if (/^wss?:\/\//i.test(raw)) return raw;
  // Same-origin: build from window.location, stripping any leading slash
  // so the join below produces a single /.
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = window.location.host; // includes :port if non-default
  const path = raw.replace(/^\/+/, '');
  return path ? `${scheme}://${host}/${path}` : `${scheme}://${host}`;
};

const WS_BASE = resolveWSBase(RAW_WS_BASE);

export const config = {
  app: {
    name: 'ProWatchly',
    version: '0.0.1',
  },
  api: {
    base: API_BASE,
    auth: {
      login: '/api/auth/login',
      register: '/api/auth/register',
      refresh: '/api/auth/refresh',
      logout: '/api/auth/logout',
      me: '/api/auth/me',
      // Click-through target for the email-link the backend embeds in the
      // welcome message. Backend route is GET with ?token=... query string;
      // we call it from the public /verify-email page when the user lands
      // there from their inbox.
      verifyEmail: '/api/auth/verify-email',
      // Password flows. `requestPasswordReset` is anonymous + always 200
      // (don't reveal email existence). `confirmPasswordReset` consumes
      // the token from the emailed `/reset-password?token=…` link.
      // `changePassword` requires the current session JWT.
      requestPasswordReset: '/api/auth/password/reset-request',
      confirmPasswordReset: '/api/auth/password/reset-confirm',
      changePassword: '/api/auth/password/change',
    },
    agents: '/api/agents',
    cameras: '/api/cameras',
    events: '/api/events',
    profile: '/api/profile',
  },
  ws: {
    alerts: `${WS_BASE}/ws/alerts`,
    // Live snapshot fanout — replaces 2 fps polling on /api/snapshots.
    // Singleton WebSocket per browser tab; see lib/snapshotStream.ts.
    snapshots: `${WS_BASE}/ws/snapshots`,
  },
  storage: {
    accessTokenKey: 'pw_access_token',
    refreshTokenKey: 'pw_refresh_token',
  },
} as const;
